import { Link } from "@tanstack/react-router";
import { DateTime } from "luxon";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
} from "@/components/ui/SidePanel";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  useFetchUserProfileQuery,
  useFetchUserDiscoverableClubsQuery,
} from "@/api/clubsApi";
import type { UserProfile, Club } from "@/api/clubsApi";
import { userDisplayName } from "@tinyclub/shared/utils";

interface ProfileSidePanelProps {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProfileSidePanel({ userId, open, onOpenChange }: ProfileSidePanelProps) {
  const {
    data: profile,
    isLoading,
    error,
  } = useFetchUserProfileQuery(
    { id: userId!, includeTopics: true },
    { skip: !userId }
  );

  const { data: discoverableClubs } = useFetchUserDiscoverableClubsQuery(
    userId!,
    { skip: !userId }
  );

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelHeader
          title="Profile"
          onClose={() => onOpenChange(false)}
        />
        <SidePanelBody className="px-5 py-0 pb-6">
          <div className="max-w-lg mx-auto space-y-6">
            {error ? (
              <div className="text-center py-8">
                <p className="text-text-secondary">
                  Failed to load profile. Please try again.
                </p>
              </div>
            ) : isLoading ? (
              <ProfilePanelSkeleton />
            ) : profile ? (
              <ProfilePanelContent
                profile={profile}
                discoverableClubs={discoverableClubs}
              />
            ) : (
              <div className="text-center py-8">
                <p className="text-text-secondary">Profile not found</p>
              </div>
            )}
          </div>
        </SidePanelBody>
      </SidePanelContent>
    </SidePanel>
  );
}

function ProfilePanelContent({
  profile,
  discoverableClubs,
}: {
  profile: UserProfile;
  discoverableClubs?: { clubs: Club }[];
}) {
  const displayName = userDisplayName(profile);

  const locationObject = profile.location_object as {
    properties?: {
      context?: {
        place?: { name?: string };
        region?: { name?: string };
      };
    };
  } | null;
  const locationCity = locationObject?.properties?.context?.place?.name;
  const locationState = locationObject?.properties?.context?.region?.name;
  const locationDisplay =
    locationCity && locationState ? `${locationCity}, ${locationState}` : profile.location_name;

  const birthdayDisplay = formatBirthdate(
    profile.birthdate,
    profile.show_year_of_birth,
    profile.show_zodiac
  );

  const joinedDisplay = profile.created_at
    ? `Joined ${DateTime.fromISO(profile.created_at).toFormat("MMMM yyyy")}`
    : null;

  const hasBioDetails =
    locationDisplay ||
    profile.job_title ||
    birthdayDisplay ||
    profile.education_name ||
    joinedDisplay;

  const hasSocialLinks =
    profile.web_personal ||
    profile.instagram ||
    profile.tiktok ||
    profile.strava ||
    profile.linkedin;

  return (
    <div className="space-y-6">
      {/* Avatar */}
      <div className="flex justify-center">
        <div className="relative">
          <div className="w-48 h-48 rounded-3xl overflow-hidden shadow-lg border border-border-tertiary">
            {profile.avatar_full || profile.avatar_medium ? (
              <img
                src={profile.avatar_full || profile.avatar_medium || ""}
                alt={displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-surface-1 flex items-center justify-center">
                <Icon name="user" size={64} className="text-text-tertiary" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Name and username section */}
      <div className="text-center space-y-1">
        <div className="flex items-baseline justify-center gap-2">
          <h2 className="text-h3 text-text-primary">{displayName}</h2>
          {profile.pronouns && (
            <span className="text-body-2 text-text-secondary">{profile.pronouns}</span>
          )}
        </div>
        {profile.username && (
          <p className="text-body-1-medium text-text-primary">@{profile.username}</p>
        )}
      </div>

      {/* Bio */}
      {profile.bio && (
        <p className="text-body-2 text-text-primary text-center">{profile.bio}</p>
      )}

      {/* Bio details panel */}
      {hasBioDetails && (
        <div className="bg-surface-1 rounded-2xl p-4 space-y-4">
          {locationDisplay && (
            <DetailRow icon="house" text={`Lives in ${locationDisplay}`} />
          )}
          {profile.job_title && (
            <DetailRow icon="briefcase" text={profile.job_title} />
          )}
          {birthdayDisplay && (
            <DetailRow icon="cake" text={`Born on ${birthdayDisplay}`} />
          )}
          {profile.education_name && (
            <DetailRow icon="graduation-cap" text={profile.education_name} />
          )}
          {joinedDisplay && (
            <DetailRow icon="calendar" text={joinedDisplay} />
          )}
        </div>
      )}

      {/* Social links */}
      {hasSocialLinks && (
        <div className="flex flex-wrap gap-2">
          {profile.web_personal && (
            <SocialPill
              icon="link"
              text={profile.web_personal}
              href={ensureHttps(profile.web_personal)}
            />
          )}
          {profile.instagram && (
            <SocialPill
              icon="instagram"
              text={profile.instagram}
              href={`https://instagram.com/${profile.instagram}`}
            />
          )}
          {profile.tiktok && (
            <SocialPill
              icon="tiktok"
              text={profile.tiktok}
              href={`https://tiktok.com/@${profile.tiktok}`}
            />
          )}
          {profile.strava && (
            <SocialPill
              icon="strava"
              text={profile.strava}
              href={`https://strava.com/athletes/${profile.strava}`}
            />
          )}
          {profile.linkedin && (
            <SocialPill
              icon="linkedin"
              text={profile.linkedin}
              href={`https://linkedin.com/in/${profile.linkedin}`}
            />
          )}
        </div>
      )}

      {/* Interests section */}
      {profile.user_topics && profile.user_topics.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-h6 text-text-primary">Interests</h3>
          <div className="flex flex-wrap gap-2">
            {profile.user_topics.map((userTopic) => {
              const isSubtopic = !!userTopic.subtopic_id;
              const topicName = isSubtopic
                ? userTopic.subtopics?.name
                : userTopic.topics?.name;
              const emoji = isSubtopic
                ? userTopic.subtopics?.parent_topic?.emoji
                : userTopic.topics?.emoji;

              return (
                <div
                  key={userTopic.id}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-border-tertiary bg-surface"
                >
                  {emoji && <span className="text-body-2">{emoji}</span>}
                  <span className="text-body-2 text-text-primary">
                    {topicName}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Clubs section */}
      {discoverableClubs && discoverableClubs.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-h6 text-text-primary">Clubs</h3>
          <div className="space-y-2">
            {discoverableClubs.map((item) => (
              <CompactClubCard key={item.clubs.id} club={item.clubs} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
        <Icon name={icon} size={18} className="text-text-primary" />
      </div>
      <span className="text-body-2 text-text-primary">{text}</span>
    </div>
  );
}

function SocialPill({ icon, text, href }: { icon: string; text: string; href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-surface-1 hover:bg-surface-2 transition-colors"
    >
      <Icon name={icon} size={18} className="text-text-primary" />
      <span className="text-body-2 text-text-primary">{text}</span>
    </a>
  );
}

function ProfilePanelSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex justify-center">
        <Skeleton className="w-48 h-48 rounded-3xl" />
      </div>
      <div className="text-center space-y-2">
        <Skeleton className="h-8 w-48 mx-auto" />
        <Skeleton className="h-5 w-24 mx-auto" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4 mx-auto" />
      </div>
      <Skeleton className="h-48 w-full rounded-2xl" />
    </div>
  );
}

function CompactClubCard({ club }: { club: Club }) {
  const locationObject = club.location_object as {
    properties?: {
      context?: {
        place?: { name?: string };
        region?: { name?: string };
      };
    };
  } | null;
  const locationCity = locationObject?.properties?.context?.place?.name;
  const locationState = locationObject?.properties?.context?.region?.name;
  const locationDisplay =
    locationCity && locationState
      ? `${locationCity}, ${locationState}`
      : club.location_name;

  return (
    <Link
      to="/clubs/$identifier"
      params={{ identifier: club.id! }}
      className="flex items-center gap-3 p-3 rounded-2xl bg-surface hover:bg-surface-1 transition-colors"
    >
      <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border border-border-tertiary">
        {club.primary_image_medium ? (
          <img
            src={club.primary_image_medium}
            alt={club.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-surface-1 flex items-center justify-center">
            <Icon name="user-group" size={20} className="text-text-tertiary" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-body-2-medium text-text-primary truncate">
          {club.name}
        </p>
        {locationDisplay && (
          <p className="text-body-3 text-text-secondary truncate">
            {locationDisplay}
          </p>
        )}
      </div>
      <Icon name="angle-right" size={16} className="text-text-tertiary flex-shrink-0" />
    </Link>
  );
}

// Helper functions

function formatBirthdate(
  birthdate: string | null | undefined,
  showYear: boolean | null | undefined,
  showZodiac: boolean | null | undefined
): string | null {
  if (!birthdate) return null;

  try {
    const date = DateTime.fromISO(birthdate);
    if (!date.isValid) return null;

    let result = date.toFormat("MMMM d");

    if (showYear) {
      result = date.toFormat("MMMM d, yyyy");
    }

    if (showZodiac) {
      const zodiac = getZodiacSign(date.month, date.day);
      if (zodiac) {
        result += ` (${zodiac})`;
      }
    }

    return result;
  } catch {
    return null;
  }
}

function getZodiacSign(month: number, day: number): string | null {
  const signs = [
    { name: "Capricorn", endMonth: 1, endDay: 19 },
    { name: "Aquarius", endMonth: 2, endDay: 18 },
    { name: "Pisces", endMonth: 3, endDay: 20 },
    { name: "Aries", endMonth: 4, endDay: 19 },
    { name: "Taurus", endMonth: 5, endDay: 20 },
    { name: "Gemini", endMonth: 6, endDay: 20 },
    { name: "Cancer", endMonth: 7, endDay: 22 },
    { name: "Leo", endMonth: 8, endDay: 22 },
    { name: "Virgo", endMonth: 9, endDay: 22 },
    { name: "Libra", endMonth: 10, endDay: 22 },
    { name: "Scorpio", endMonth: 11, endDay: 21 },
    { name: "Sagittarius", endMonth: 12, endDay: 21 },
    { name: "Capricorn", endMonth: 12, endDay: 31 },
  ];

  for (const sign of signs) {
    if (month < sign.endMonth || (month === sign.endMonth && day <= sign.endDay)) {
      return sign.name;
    }
  }

  return "Capricorn";
}

function ensureHttps(url: string): string {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `https://${url}`;
}
